/**
 * API Platform helpers — IRI extraction, normalization, and form value utilities.
 */

/**
 * Extract numeric ID from API Platform IRI (e.g., "/api/civilites/40" → 40).
 */
export function extractIdFromIri(iri: string): number | null {
  if (!iri || typeof iri !== "string") return null;
  const parts = iri.split("/");
  const id = parseInt(parts[parts.length - 1], 10);
  return Number.isNaN(id) ? null : id;
}

/**
 * Create an IRI from resource type and ID (e.g., "civilites", 40 → "/api/civilites/40").
 */
export function createIri(resourceType: string, id: number | string): string {
  return `/api/${resourceType}/${id}`;
}

/**
 * Normalize API Platform collection response to consistent format.
 * Extracts numeric id from @id IRI and adds it as 'id' property.
 */
export function normalizeApiPlatformCollection<
  T extends Record<string, unknown> = Record<string, unknown>,
>(data: unknown): T[] {
  const member = (data as Record<string, unknown>)?.member || data || [];
  if (!Array.isArray(member)) {
    return [];
  }
  return member.map((item: Record<string, unknown>) => ({
    ...item,
    id: item.id ?? extractIdFromIri(item["@id"] as string),
  })) as unknown as T[];
}

/**
 * Normalize API Platform collection to flat array (Hydra or non-Hydra format).
 */
export function normalizeCollection(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    return (obj.member as unknown[]) ?? (obj["hydra:member"] as unknown[]) ?? [];
  }
  return [];
}

/**
 * Normalized collection response shape returned by `normalizeCollectionResponse`.
 */
export interface CollectionResponse<T> {
  member: T[];
  totalItems: number;
}

/**
 * Normalize any API Platform collection response (Hydra, JSON-LD, or plain array)
 * into a consistent `{ member, totalItems }` shape.
 */
export function normalizeCollectionResponse<T = Record<string, unknown>>(
  data: unknown,
): CollectionResponse<T> {
  if (Array.isArray(data)) return { member: data as T[], totalItems: data.length };
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const member = (obj["hydra:member"] ?? obj.member ?? []) as T[];
    return {
      member: Array.isArray(member) ? member : [],
      totalItems: (obj["hydra:totalItems"] as number) ?? member.length,
    };
  }
  return { member: [], totalItems: 0 };
}

/**
 * Transform data for API Platform submission — converts numeric IDs to IRIs for relations.
 */
export function transformForApiPlatform(
  data: Record<string, unknown>,
  relations: Record<string, string> = {},
): Record<string, unknown> {
  const transformed = { ...data };

  Object.entries(relations).forEach(([field, resourceType]) => {
    if (transformed[field] !== null && transformed[field] !== undefined) {
      transformed[field] = createIri(resourceType, transformed[field] as number);
    }
  });

  return transformed;
}

/**
 * Extract stable form value (IRI preferred, fallback to numeric id).
 * Use in form select/autocomplete `value` bindings for stable keys.
 */
export function getItemValue(item: unknown): string {
  if (!item || typeof item !== "object") return String(item ?? "");
  const obj = item as Record<string, unknown>;
  return (obj["@id"] as string) ?? String(obj.id ?? "");
}

/**
 * Extract human-readable label from API resource.
 * Priority: libelle > raisonSociale > nom > code > email > numero > id
 */
export function getItemLabel(item: unknown): string {
  if (!item || typeof item !== "object") return String(item ?? "");
  const obj = item as Record<string, unknown>;
  return (
    (obj.libelle as string) ??
    (obj.raisonSociale as string) ??
    (obj.nom as string) ??
    (obj.code as string) ??
    (obj.email as string) ??
    (obj.numero as string) ??
    String(obj.id ?? "")
  );
}
